import { DotsVerticalIcon, PlusCircleIcon } from "@heroicons/react/outline";
import { IAttractionNoShowTimeTicketForm, ISimpleEventDetail, ISubTicketForm, ISubTicketItem, IUsuallyTypeTicketForm, ProductType, SupportedLanguage } from "interfaces";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
// import { SubTicketForm } from "../../forms/subTicketForm";
import { useCreate, useUpdate } from "@refinedev/core";
import toast from "react-hot-toast";
import { DropdownMenu } from "components/dropdownMenu";
import { NoShowTimeTicketForm } from "../../forms/noShowTimeTicketForm";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { formatNumber } from "utills/helpers";
import { TooltipCircle } from "components/tooltipCircle";
import { ConfirmModal } from "components/confirmModal";
import { SubTicketForm } from "../../forms/subTicketForm";
import { DuplicateTicketModal } from "../../duplicateModals/ticket";
import { DuplicateSubTicketModal } from "../../duplicateModals/subTicket";
import { FullScreenLoading } from "components/fullScreenLoading";

interface SubTicketModal {
  open: boolean;
  submit: (data: ISubTicketForm) => void;
  defaultValues: ISubTicketItem | null;
}

interface SubTicketsProps {
  ticketIndex: number;
  eventDetailData: ISimpleEventDetail;
  showTimeTicket?: boolean;
  _moveField?: (order: string[], ticketMovedId: string) => void;
  setOpenLogModalId: (id: string) => void;
  _createNoShowTimeTicket?: (data: IAttractionNoShowTimeTicketForm, message?: string, callback?: () => void) => void;
}
interface NoPhaseTicketModal {
  open: boolean;
  submit: (data: IAttractionNoShowTimeTicketForm) => void;
  defaultValues: ISubTicketItem | null;
}

const SUB_TICKET_MODAL_CONFIG: SubTicketModal = {
  open: false,
  submit: (data: ISubTicketForm) => {},
  defaultValues: null,
}

const NO_PHASE_TICKET_MODAL_CONFIG: NoPhaseTicketModal = {
  open: false,
  submit: (data: IAttractionNoShowTimeTicketForm) => {},
  defaultValues: null,
}

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  title: "",
  submit: () => {}
}

const INITIAL_DUPLICATE_TICKET_MODAL_CONFIG = {
  type: "",
  submit: (data: any) => {}
}

export const SubTickets = ({ticketIndex, eventDetailData, showTimeTicket = true, _moveField, setOpenLogModalId, _createNoShowTimeTicket}: SubTicketsProps) => {
  const [subTicketModalConfig, setSubTicketModalConfig] = useState(SUB_TICKET_MODAL_CONFIG);
  const [subTicketConfirmModalConfig, setSubTicketConfirmModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);
  const [noPhaseTicketModalConfig, setNoPhaseTicketModalConfig] = useState(NO_PHASE_TICKET_MODAL_CONFIG);
  const [duplicateTicketModalConfig, setDuplicateTicketModalConfig] = useState(INITIAL_DUPLICATE_TICKET_MODAL_CONFIG)

  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);

  const { mutate, isLoading: isCreating } = useCreate();
  const { mutate: updateMutation, isLoading: isUpdating } = useUpdate();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IUsuallyTypeTicketForm>(); // retrieve all hook methods

  const { fields, append, move } = useFieldArray({
    control,
    name: `tickets.${ticketIndex}.sub_tickets`,
    keyName: "field_id"
  });

  useEffect(() => {
    if (draggedItemId) {
      _moveField && _moveField(fields.map(field => field.id ?? ""), draggedItemId);
      setDraggedItemId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedItemId])

  const _onCloseModal = () => {
    setSubTicketModalConfig(SUB_TICKET_MODAL_CONFIG);
  }

  const _onCloseNoPhaseTicketModal = () => {
    setNoPhaseTicketModalConfig(NO_PHASE_TICKET_MODAL_CONFIG);
  }

  const _onCloseSubTicketConfirmModal = () => {
    setSubTicketConfirmModalConfig({
      ...INITIAL_ACTION_MODAL_CONFIG,
      title: subTicketConfirmModalConfig.title
    });
  }

  const _onCloseDuplicateTicketModal = () => {
    setDuplicateTicketModalConfig(INITIAL_DUPLICATE_TICKET_MODAL_CONFIG);
  }

  const _createSubTicket = (submitValues: ISubTicketForm, successMessage?: string, callBack?: () => void) => {
    const ticketId = get(watch("tickets")[ticketIndex].ticket, "id", "");
    mutate(
      {
        resource: "content/tickets/attractions/regular/sub",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          parent_ticket_id: ticketId,
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          const newSubTicket = get(data, "data.data", null) as ISubTicketItem;
          append(newSubTicket);
          toast.success(successMessage ?? "Create sub-ticket successfully");
          _onCloseModal();
          callBack && callBack();
        },
      }
    );
  }

  const _updateSubTicket = (subTicketIndex: number, submitValues: ISubTicketForm) => {
    const ticketId = get(watch("tickets")[ticketIndex].sub_tickets[subTicketIndex], "id", "");
    updateMutation(
      {
        resource: "content/tickets/attractions/regular/sub",
        id: ticketId,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          const updatedSubTicket = get(data, "data.data", null) as ISubTicketItem;
          if (updatedSubTicket) {
            setValue(`tickets.${ticketIndex}.sub_tickets.${subTicketIndex}`, updatedSubTicket);
          }
          toast.success("Update sub-ticket successfully");
          _onCloseModal();
        },
      }
    );
  }

  const _updateNoPhaseTicket = (id: string, submitValues: IAttractionNoShowTimeTicketForm) => {
    updateMutation(
      {
        resource: "content/tickets/attractions/regular/single",
        id,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: (data) => {
          const updatedTicket = get(data, "data.data", null) as ISubTicketItem;
          console.log(updatedTicket);
          if (updatedTicket) {
            const updatedTicketIndex = subTicketsData.findIndex(subTicket => get(subTicket, `id`, '') === id);
            console.log(updatedTicketIndex);
            setValue(`tickets.${ticketIndex}.sub_tickets.${updatedTicketIndex}`, updatedTicket);
          }
          toast.success("Update ticket successfully");
          _onCloseNoPhaseTicketModal();
        },
      }
    );
  }

  const _duplicateSubTicket =(updatedTicket: any, ticket: ISubTicketItem) => {
    const duplicatedTicket = {
      ...ticket,
      ...updatedTicket
    }
    _createSubTicket(duplicatedTicket, "Duplicate successfully", _onCloseDuplicateTicketModal);
  }

  const _duplicateTicket =(updatedTicket: any, ticket: ISubTicketItem) => {
    const duplicatedTicket = {
      ...ticket,
      ...updatedTicket
    }
    _createNoShowTimeTicket && _createNoShowTimeTicket(duplicatedTicket, "Duplicate successfully", _onCloseDuplicateTicketModal);
  }

  const handleDrag = ({ source, destination }: any) => {
    if (destination && (source.index !== destination.index)) {
      move(source.index, destination.index);
      setDraggedItemId(fields[source.index].id ?? null);
    }
  };

  const handleClickMove = (sourceIndex: number, destinationIndex: number) => {
    if (sourceIndex !== destinationIndex) {
      move(sourceIndex, destinationIndex);
      setDraggedItemId(fields[sourceIndex].id ?? null);
    }
  }

  const subTicketsData = watch("tickets")[ticketIndex].sub_tickets;
  const showtimes = watch("showtimes");
  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN) ? "en" : eventDetailData.languages[0] ?? 'vi';

  return (
    <>
      <DragDropContext onDragEnd={handleDrag}>
        <div className="grid sm:grid-cols-6 lg:gap-x-6">
          <Droppable droppableId="subticket-items" isDropDisabled={isUpdating}>
            {(provided, snapshot) => (
              <div
                className="col-span-full"
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {fields.map((subTicketField, subTicketIndex: number) => (
                  <Draggable
                    key={`subticket[${subTicketIndex}]`}
                    draggableId={`item-${subTicketIndex}`}
                    isDragDisabled={showTimeTicket || isUpdating} // disable in phase ticket
                    index={subTicketIndex}
                  >
                    {(provided, snapshot) => (
                      <div
                        className={`border rounded-lg mb-4 ${
                          get(errors, `tickets.${subTicketIndex}`, "")
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        key={subTicketField.field_id}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <div
                          className="box-container p-0"
                          {...provided.dragHandleProps}
                        >
                          <div className="w-full flex items-start gap-x-3 px-4 py-4 sm:px-6 hover:cursor-pointer">
                            <div className="flex-1 flex flex-col xl:flex-row xl:justify-between xl:items-center gap-2 sm:gap-3">
                              <p className="flex-1 font-bold" style={{wordBreak: "break-word"}}>
                                {!isEmpty(showtimes) 
                                  ? `${showtimes.find(showtime => showtime.id === get(subTicketsData[subTicketIndex], "showtime", ""))?.time} (${get(subTicketsData[subTicketIndex], `type_name.${firstLanguage}`, "")})`
                                  : get(subTicketsData[subTicketIndex], `category.${firstLanguage}`, "")}
                              </p>
                              <div className="flex-1 flex flex-row items-center xl:justify-end gap-x-3 text-sm">
                                <div className="flex flex-wrap items-center xl:justify-end gap-x-3 gap-y-2">
                                  <p>
                                    {get(subTicketsData[subTicketIndex], "sell_from", "") 
                                      ? moment(get(subTicketsData[subTicketIndex], "sell_from", "")).format("HH:mm DD/MM/YYYY") 
                                      : ""} 
                                    {' '}-{' '} 
                                    {get(subTicketsData[subTicketIndex], "sell_to", "") 
                                      ? moment(get(subTicketsData[subTicketIndex], "sell_to", "")).format("HH:mm DD/MM/YYYY")
                                      : ""}
                                  </p>
                                  <p>
                                    {formatNumber(get(subTicketsData[subTicketIndex], "price", 0))} {eventDetailData.currency}
                                    {' '}
                                    <TooltipCircle text={`Original price: ${formatNumber(get(subTicketsData[subTicketIndex], "original_price", 0))} ${eventDetailData.currency} <br/>Sell price: ${formatNumber(get(subTicketsData[subTicketIndex], "price", 0))} ${eventDetailData.currency}`} />
                                  </p>
                                  <div className="shrink-0 flex flex-row gap-x-2">
                                    {subTicketsData[subTicketIndex]?.is_active ? (
                                      <span
                                        className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-green-100 ring-green-100 text-green-700`}
                                      >
                                        Active
                                      </span>
                                    ) : (
                                      <span
                                        className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-magnolia-500 text-neutral-800 ring-magnolia-600`}
                                      >
                                        Inactive
                                      </span>
                                    )}
                                    {subTicketsData[subTicketIndex]?.is_active ? (subTicketsData[subTicketIndex]?.hide_in_ui) ? (
                                      <span
                                        className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-magnolia-500 text-neutral-800 ring-magnolia-600`}
                                      >
                                        Hide in PROD
                                      </span>
                                    ) : (
                                      <span
                                        className={`w-fit inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ring-1 ring-inset bg-green-100 ring-green-100 text-green-700`}
                                      >
                                        Show in PROD
                                      </span>
                                    ) : <></>}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="shrink-0 self-center">
                              <DropdownMenu
                                label={
                                  <DotsVerticalIcon className="w-4 h-4 text-brand-500" />
                                }
                                items={[
                                  {
                                    label: "Edit",
                                    onClick: () => showTimeTicket ? setSubTicketModalConfig({
                                      open: true,
                                      submit: (data) => _updateSubTicket(subTicketIndex, data),
                                      defaultValues: subTicketsData[subTicketIndex]
                                    }) : setNoPhaseTicketModalConfig({
                                      open: true,
                                      defaultValues: subTicketsData[subTicketIndex],
                                      submit: (data) => _updateNoPhaseTicket(get(subTicketsData[subTicketIndex], `id`, ''), data)
                                    }),
                                  },
                                  {
                                    label: subTicketsData[subTicketIndex]?.is_active ? "Inactive" : "Active",
                                    onClick: () => setSubTicketConfirmModalConfig({
                                      open: true,
                                      title: `Are you sure you want to ${subTicketsData[subTicketIndex]?.is_active ? "deactivate" : "activate"} this ${showTimeTicket ? "sub-ticket" : "ticket"}?`,
                                      submit: () => {
                                        showTimeTicket 
                                        ? _updateSubTicket(subTicketIndex, {
                                          ...subTicketsData[subTicketIndex],
                                          is_active: !subTicketsData[subTicketIndex]?.is_active,
                                        }) 
                                        : 
                                        _updateNoPhaseTicket(get(subTicketsData[subTicketIndex], `id`, ''), {
                                          ...subTicketsData[subTicketIndex] as IAttractionNoShowTimeTicketForm,
                                          is_active: !subTicketsData[subTicketIndex].is_active,
                                        });
                                      }
                                    })
                                  },
                                  ...(subTicketsData[subTicketIndex]?.is_active
                                    ? [
                                        {
                                          label: subTicketsData[subTicketIndex]?.hide_in_ui ? "Show in PROD" : "Hide in PROD",
                                          onClick: () => setSubTicketConfirmModalConfig({
                                            open: true,
                                            title: `Are you sure you want to ${subTicketsData[subTicketIndex]?.hide_in_ui ? "show" : "hide"} this ${showTimeTicket ? "sub-ticket" : "ticket"} in PROD?`,
                                            submit: () => {
                                              showTimeTicket 
                                              ?
                                              _updateSubTicket(subTicketIndex, {
                                                ...subTicketsData[subTicketIndex],
                                                hide_in_ui: !subTicketsData[subTicketIndex]?.hide_in_ui,
                                              })
                                              :
                                              _updateNoPhaseTicket(get(subTicketsData[subTicketIndex], `id`, ''), {
                                                ...subTicketsData[subTicketIndex] as IAttractionNoShowTimeTicketForm,
                                                hide_in_ui: !subTicketsData[subTicketIndex].hide_in_ui,
                                              });
                                            }
                                          })
                                        },
                                      ]
                                    : []),
                                    ...((subTicketIndex !== 0 && !showTimeTicket)
                                    ? [
                                      {
                                        label: "Move up",
                                        onClick: () =>
                                          handleClickMove(subTicketIndex, subTicketIndex - 1),
                                      },
                                    ]
                                  : []),
                                  ...((subTicketIndex !== fields.length - 1 && !showTimeTicket)
                                  ? [
                                      {
                                        label: "Move Down",
                                        onClick: () =>
                                          handleClickMove(subTicketIndex, subTicketIndex + 1),
                                      },
                                    ]
                                  : []),
                                  {
                                    label: "Duplicate",
                                    onClick: () => !showTimeTicket ? setDuplicateTicketModalConfig({
                                      type: "ticket",
                                      submit: (data) => _duplicateTicket(data, subTicketsData[subTicketIndex])
                                    }) 
                                    : 
                                    setDuplicateTicketModalConfig({
                                      type: "sub-ticket",
                                      submit: (data) => _duplicateSubTicket(data, subTicketsData[subTicketIndex])
                                    })
                                  },
                                  {
                                    label: "Log",
                                    onClick: () => setOpenLogModalId(subTicketsData[subTicketIndex]?.id ?? "")
                                  }
                                ]}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}

                <div className="text-center text-sm">
                  {isEmpty(fields) ? <p>No Tickets</p> : <></>}
                </div>
                {showTimeTicket ? (
                  <div className="w-full text-center">
                    <button
                      type="button"
                      className="flex flex-row items-center mx-auto mt-4 w-fit py-2.5 px-4 text-sm bg-white text-brand-500 active:bg-gray-50"
                      onClick={() => setSubTicketModalConfig({
                        open: true,
                        submit: (data) => _createSubTicket(data),
                        defaultValues: null
                      })}
                    >
                      <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                      <span className="shrink-0 text-sm font-medium">Add more Sub-ticket</span>
                    </button>
                  </div>
                ) : <></>}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      {showTimeTicket ? (
        <SubTicketForm 
          eventDetailData={eventDetailData}
          open={subTicketModalConfig.open}
          onClose={_onCloseModal}
          showTimes={showtimes}
          title={subTicketModalConfig.defaultValues ? "Edit Sub-Ticket" : "Create your Sub-Ticket per Phase"}
          defaultValues={subTicketModalConfig.defaultValues}
          onSubmit={subTicketModalConfig.submit}
        />
      ) : (
        <NoShowTimeTicketForm
          defaultValues={noPhaseTicketModalConfig.defaultValues}
          eventDetailData={eventDetailData}
          open={noPhaseTicketModalConfig.open}
          onClose={_onCloseNoPhaseTicketModal}
          productType={ProductType.TICKET}
          onSubmit={noPhaseTicketModalConfig.submit}
          title="Edit Ticket"
        />
      )}
      <ConfirmModal 
        key="active-sub-ticket"
        title={subTicketConfirmModalConfig.title}
        content=""
        open={subTicketConfirmModalConfig.open}
        onConfirm={subTicketConfirmModalConfig.submit}
        onClose={_onCloseSubTicketConfirmModal}
      />
      {!showTimeTicket ? (
        <DuplicateTicketModal
          open={duplicateTicketModalConfig.type === "ticket"}
          onClose={_onCloseDuplicateTicketModal}
          onSubmit={duplicateTicketModalConfig.submit}
          eventDetailData={eventDetailData}
        />
      ) : (
        <DuplicateSubTicketModal 
          open={duplicateTicketModalConfig.type === "sub-ticket"}
          onClose={_onCloseDuplicateTicketModal}
          onSubmit={duplicateTicketModalConfig.submit}
          eventDetailData={eventDetailData}
          showtimes={showtimes}
        />
      )}
      {(isCreating || isUpdating) ? <FullScreenLoading /> : <></>}
    </>
  )
}